import React, { useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HPButton from "@components/common/Button";
import BarRow from "./BarRow";
import type { BarCustomization } from "@store/slices/chart-config-slice";

interface BarEntry {
  name: string;
  color: string;
  isHiddenInDataset?: boolean;
}

interface BarCustomizationSectionProps {
  bars: BarEntry[];
  barCustomization: BarCustomization;
  dataSorting: string;
  colorPalette: string;
  onToggleVisibility: (barName: string) => void;
  onToggleAll: (visible: boolean) => void;
  onPinChange: (barName: string, position: "none" | "first" | "last") => void;
  onLabelChange: (barName: string, label: string) => void;
  onResetLabel: (barName: string) => void;
  onCustomOrderChange: (newOrder: string[]) => void;
}

const BarCustomizationSection: React.FC<BarCustomizationSectionProps> = ({
  bars,
  barCustomization,
  dataSorting,
  colorPalette,
  onToggleVisibility,
  onToggleAll,
  onPinChange,
  onLabelChange,
  onResetLabel,
  onCustomOrderChange,
}) => {
  const [editingBarName, setEditingBarName] = useState<string | null>(null);
  const [editBarValue, setEditBarValue] = useState("");

  const isCustomOrder = dataSorting === "custom";
  const isCustomPalette = colorPalette === "Custom (per-bar)";

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Build ordered list for display
  const orderedBars = useMemo(() => {
    if (!isCustomOrder || barCustomization.customOrder.length === 0) return bars;
    const orderIndex = new Map(
      barCustomization.customOrder.map((n, i) => [n, i]),
    );
    return [...bars].sort((a, b) => {
      const ai = orderIndex.has(a.name) ? orderIndex.get(a.name)! : bars.length;
      const bi = orderIndex.has(b.name) ? orderIndex.get(b.name)! : bars.length;
      return ai - bi;
    });
  }, [bars, isCustomOrder, barCustomization.customOrder]);

  const sortableIds = orderedBars.map((b) => b.name);
  const visibleCount = bars.filter(
    (b) => !barCustomization.hiddenBars.includes(b.name),
  ).length;

  // Show All only ever re-shows bars whose source is still visible in the
  // dataset — a dataset-hidden bar stays hidden until restored in Questions.
  const eligibleBars = bars.filter((b) => !b.isHiddenInDataset);
  const visibleEligibleCount = eligibleBars.filter(
    (b) => !barCustomization.hiddenBars.includes(b.name),
  ).length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(active.id as string);
    const newIndex = sortableIds.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onCustomOrderChange(arrayMove(sortableIds, oldIndex, newIndex));
    }
  };

  const handleStartEdit = (barName: string, currentLabel: string) => {
    setEditingBarName(barName);
    setEditBarValue(currentLabel);
  };

  const handleSaveEdit = () => {
    if (editingBarName && editBarValue.trim()) {
      onLabelChange(editingBarName, editBarValue.trim());
    }
    setEditingBarName(null);
    setEditBarValue("");
  };

  const handleCancelEdit = () => {
    setEditingBarName(null);
    setEditBarValue("");
  };

  const handleResetLabel = (barName: string) => {
    onResetLabel(barName);
    if (editingBarName === barName) {
      setEditingBarName(null);
      setEditBarValue("");
    }
  };

  if (bars.length === 0) return null;

  return (
    <Accordion
      defaultExpanded
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Bar Customization
          </Typography>
          <Chip
            label={`${visibleCount}/${bars.length} visible`}
            size="small"
            color={visibleCount === bars.length ? "default" : "primary"}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <HPButton
              size="small"
              variant="outlined"
              onClick={() => onToggleAll(true)}
              disabled={visibleEligibleCount === eligibleBars.length}
            >
              Show All
            </HPButton>
            <HPButton
              size="small"
              variant="outlined"
              onClick={() => onToggleAll(false)}
              disabled={visibleCount === 0}
            >
              Hide All
            </HPButton>
          </Box>

          <Divider />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Box
              sx={{
                maxHeight: 400,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {orderedBars.map((bar) => {
                  const isHidden = barCustomization.hiddenBars.includes(
                    bar.name,
                  );
                  const overrideLabel =
                    barCustomization.labelOverrides[bar.name];
                  const displayName = overrideLabel ?? bar.name;
                  const manualColor = barCustomization.manualColors[bar.name];
                  const resolvedColor = manualColor ?? bar.color;
                  const pinPosition: "none" | "first" | "last" =
                    barCustomization.pinnedFirst === bar.name
                      ? "first"
                      : barCustomization.pinnedLast === bar.name
                        ? "last"
                        : "none";
                  const isEditing = editingBarName === bar.name;

                  return (
                    <BarRow
                      key={bar.name}
                      name={bar.name}
                      displayName={displayName}
                      isOverridden={Boolean(overrideLabel)}
                      isHidden={isHidden}
                      isHiddenInDataset={bar.isHiddenInDataset}
                      pinPosition={pinPosition}
                      isCustomOrder={isCustomOrder}
                      showPinControl={!isCustomOrder}
                      showColorSwatch={isCustomPalette}
                      seriesColor={resolvedColor}
                      isEditing={isEditing}
                      editValue={editBarValue}
                      onToggleVisibility={() => onToggleVisibility(bar.name)}
                      onPinChange={(pos) => onPinChange(bar.name, pos)}
                      onStartEdit={() => handleStartEdit(bar.name, displayName)}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit}
                      onEditValueChange={setEditBarValue}
                      onResetLabel={() => handleResetLabel(bar.name)}
                    />
                  );
                })}
              </SortableContext>
            </Box>
          </DndContext>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BarCustomizationSection;
